import { clsx } from 'clsx';
import { useRouter } from 'next/router';

import ImageSlider from '~/src/common/components/ImageSlider/ImageSlider';
import { useImageSliderData } from '~/src/common/components/ImageSlider/useImageSliderData';
import { useGetProductCartFreeArticleCoupons } from '~/src/common/hooks/cart/useGetCartFreeArticleCoupons';
import I18n from '~/src/common/services/I18n';
import Tracker, { CommonCatalogEventProperties } from '~/src/common/services/Tracker';
import { ProductDetailed } from '~/src/common/typings/product';
import { SPECIFIC_TAG_ATTRIBUTE_KEY } from '~/src/common/utils/product';
import { useGetCategories } from '~/src/queries/api-ecom/generated/api-ecom';
import useBreadcrumbs from '~/src/screens/CatalogContent/ContentScreen/hooks/useBreadcrumbs';
import ProductAdditionalInformation from '~/src/screens/CatalogContent/ContentScreen/ProductScreen/components/ProductAdditionalInformation/ProductAdditionalInformation';
import ProductInformation from '~/src/screens/CatalogContent/ContentScreen/ProductScreen/components/ProductInformation/ProductInformation';
import { ProductXSell } from '~/src/screens/CatalogContent/ContentScreen/ProductScreen/components/ProductXSell/ProductXSell';
import { CatalogContentBreadcrumb } from '~/src/screens/CatalogContent/ContentScreen/shared/CatalogContentBreadcrumb/CatalogContentBreadcrumb';
import { CatalogContentLinkQueryParams } from '~/src/screens/CatalogContent/ContentScreen/types';

import ProductPriceAndPick from './components/ProductPriceAndPick/ProductPriceAndPick';
import PrimaryInformation from './components/ProductPrimaryInformation/ProductPrimaryInformation';
import SecondaryInformation from './components/ProductSecondaryInformation/ProductSecondaryInformation';
import ShortDescription from './components/ProductShortDescription/ProductShortDescription';
import { hiddenAttributesKeys, hiddenAttributesValues } from './constants';
import styles from './ProductScreen.module.scss';

interface Props {
  product: ProductDetailed;
  refetchProduct: () => Promise<unknown>;
  analyticsProperties: CommonCatalogEventProperties;
}

export const ProductContent = (props: Props) => {
  const { data: categories } = useGetCategories();

  const { query } = useRouter<CatalogContentLinkQueryParams>();
  const { category, subcategory } = query;

  const sliderImages = useImageSliderData(props.product.images);

  // Extraction du nombre de produits gratuit et des produits liés
  const freeQuantity = useGetProductCartFreeArticleCoupons(props.product.id);

  // Suppression des attributs pas interessants
  const filteredAttributes = props.product.attributes.filter(attr => {
    const isEmptyVal = !attr.value;
    const isHiddenKey = [...hiddenAttributesKeys, SPECIFIC_TAG_ATTRIBUTE_KEY].includes(attr.key);
    const isHiddenVal = hiddenAttributesValues.includes(String(attr.value ?? '').toLowerCase());
    const isCustomHidden = attr.key === 'netweight' && attr.valueUnit === 'N/A';
    return !(isEmptyVal || isHiddenKey || isHiddenVal || isCustomHidden);
  });

  const categoriesBreadcrumbs = useBreadcrumbs(categories, props.product.breadcrumbs);

  const mainCategorySlug = props.product.breadcrumbs?.[0]?.slug;
  const categoryUrl = mainCategorySlug ? `/categorie/${mainCategorySlug}` : undefined;

  const handleImageSliderArrowClick = () => {
    Tracker.sendEvent('roll out photo carousel', {
      'product category': category,
      'product name': props.product.name,
      'product subcategory': subcategory,
      'photo event source': 'product page',
      'opened from': 'slider',
    });
  };

  const handleImageSliderThumbnailClick = (eventSource: 'product page' | 'photo modal') => {
    Tracker.sendEvent('roll out photo carousel', {
      'product category': category,
      'product name': props.product.name,
      'product subcategory': subcategory,
      'photo event source': eventSource,
      'opened from': 'thumbnails',
    });
  };

  return (
    <>
      <div className={styles.container}>
        <CatalogContentBreadcrumb
          item={props.product}
          analyticsProperties={props.analyticsProperties}
        />

        <div className={styles.content}>
          <div className={styles.grid}>
            <ImageSlider
              className={styles.productImageSlider}
              images={sliderImages}
              title={props.product.name}
              onArrowClick={handleImageSliderArrowClick}
              onThumbnailClick={handleImageSliderThumbnailClick}
            />

            <PrimaryInformation
              className={styles.primaryInformationGridPosition}
              product={props.product}
              categoriesBreadcrumbs={categoriesBreadcrumbs}
            />

            <ProductPriceAndPick
              className={styles.pickerGridPosition}
              product={props.product}
              freeProductQuantity={freeQuantity}
              onRefresh={props.refetchProduct}
              categoriesBreadcrumbs={categoriesBreadcrumbs}
            />

            <SecondaryInformation
              className={clsx(styles.secondaryInformationGridPosition, styles.asideBlock)}
              product={props.product}
            />

            <ShortDescription
              className={clsx(styles.shortDescriptionGridPosition, styles.asideBlock)}
              product={props.product}
            />
          </div>
          <ProductXSell
            mode="SIMILAR"
            title={I18n.t('view-product-screen.similar-products')}
            articleId={props.product.id}
            seeAllLink={categoryUrl}
          />
          <ProductXSell
            mode="COMPLEMENTARY"
            title={I18n.t('view-product-screen.complementary-products')}
            articleId={props.product.id}
          />
          <ProductInformation product={props.product} />
          <ProductAdditionalInformation attributes={filteredAttributes} />
        </div>
      </div>
    </>
  );
};
